/**
 * Component for populating the data in public `NotificationCorpayAutoPanel` panel
 */

import { Button, Col, Drawer, Form, Select, Modal, Row, Skeleton, Progress } from 'antd';
import {
    clone,
    forEach,
    get,
    includes,
    isEmpty,
    isUndefined,
    keys,
    last,
    map,
    sumBy,
    toNumber,
} from 'lodash';

import React, { lazy, Suspense, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withDateFormatHandler } from '../../components/common/DateFormatHandler';
import { withNumberFormatHandler } from '../../components/common/NumberFormatHandler';
import CountDownTimer from '../../components/common/CountDownTimer';
import {
    getPaymentSpotRateAction, bookDealRequestAction
} from '../../store/payments/actions';
import { getIsMobile } from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';
import corpayLogo from '../../assets/corpayLogo.png';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { AppCommonState } from '../../store/common/types';

const ModalWithSpinner = lazy(
    () => import('../../components/common/ModalWithSpinner')
);

interface IProps {
    readonly visible: boolean;
    readonly closePanel?: (refetchList?: boolean) => void;
    readonly currencies: [{
        value: string
    }];
    readonly totalPayment: number;
    readonly company: {
        CompanyId: string,
        Name: string
    };
    readonly setCorpayInstructPaymentDetails: any;
    readonly corpayInstructPaymentDetails: {
        OrderId: '',
        ClientAccessToken: '',
        SelectedCurrency: ''
    };
    readonly setSpotRateDetails: any;
    readonly spotRateDetails: {
        spotRate: 0,
        amountToBePaid: 0,
        amountToBeSettled: 0
    };
    formatCurrency: (amount: number) => JSX.Element;
}

const NotificationCorpayPaymentAutoPanel: React.FC<IProps> = ({
    visible,
    closePanel,
    currencies,
    totalPayment,
    company,
    setCorpayInstructPaymentDetails,
    corpayInstructPaymentDetails,
    setSpotRateDetails,
    spotRateDetails,
    formatCurrency
}: IProps) => {
    const { Option } = Select;
    const isMobile = getIsMobile();
    const panelTitle = 'Corpay';
    const dispatch = useDispatch();
    const [payBtnDisabled, setPayBtnDisabled] = useState<boolean>(true);
    const [spotRateLoading, setSpotRateLoading] = useState<boolean>(false);
    const [selectedCurrency, setSelectedCurrency] = useState<string>("");
    const [displayCountdown, setDisplayCountdown] = useState<boolean>(false);
    const [timeLimit, setTimeLimit] = useState<number>(0);
    const [timesUp, setTimesUp] = useState<boolean>(false);
    const [bookDealDetails, setBookDealDetails] = useState<{
        quoteId: string;
        clientAccessToken: string;
        companyId: any;
    }>({
        quoteId: '',
        clientAccessToken: '',
        companyId: '',
    });
    const appState: AppCommonState = useSelector(
        (state: ApplicationState) => state.app
    );
    const currencyCode = appState.currencyCode;

    /**
     * Function called when drawer or modal is closed.
     */
    const handleClosePanel = () => {
        if (closePanel) closePanel();
        setSpotRateDetails({});
        setPayBtnDisabled(true);
    };

    /**
     * Function called to format amount to be paid.
     */
    const formatAmountToBePaid = (currency: string, amount: number) => {
        // let locale = NSLocale(localeIdentifier: currency)
        const formatCurrency = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency
        });
        const convertedAmount = formatCurrency.format(amount);
        const result = convertedAmount.replace(/[\d\., ]/g, '')
        const currencySymbol = result.charAt(result.length - 1);
        const formattedAmount = amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return currencySymbol + formattedAmount;
    }

    /**
     * check if time is up then refetch spot rate.
     */
    const refetchSpotRate = () => {
        if (timesUp === true) {
            setTimesUp(false)
            onCorpayPaymentOptionChoose(selectedCurrency)
        }
    };
    useEffect(refetchSpotRate, [timesUp]);

    /**
         * Function for handling the spot rate when chosen
         */
    const onCorpayPaymentOptionChoose = (
        currency: string,
    ) => {
        setDisplayCountdown(false)
        setSelectedCurrency(currency)
        setSpotRateDetails({});
        setSpotRateLoading(true)
        const payload = {
            "SettlementCurrency": currency,
            "Amount": totalPayment,
            "CompanyId": company.CompanyId
        }

        dispatch(
            getPaymentSpotRateAction(
                payload, (response: DynamicObject) => {
                    getPaymentSpotRateCallback(response)
                }
            )
        );
    };

    const getPaymentSpotRateCallback = (
        response: DynamicObject
    ) => {
        setSpotRateLoading(false)
        if (get(response, 'IsSuccess') === false) {

            return Modal.error({
                title: 'Error',
                content: get(
                    response,
                    'Messages.0',
                    'Failed to get the Spot Rate'
                ),
            });
        } else {
            setTimeLimit(response.CorpaySpotRateTimeLimit)
            setDisplayCountdown(true)
            setPayBtnDisabled(false)
            setSpotRateDetails({
                ...spotRateDetails,
                spotRate: 1/response.content.rate.value,
                amountToBePaid: totalPayment,
                amountToBeSettled: response.content.settlement.amount
            });

            setBookDealDetails({
                ...bookDealDetails,
                quoteId: response.content.quoteId,
                clientAccessToken: response.ClientAccessToken,
                companyId: company.CompanyId
            });
        }

    }

    const getBookDealRequestCallback = (
        response: DynamicObject
    ) => {
        if (get(response, 'IsSuccess') === false) {

            return Modal.error({
                title: 'Error',
                content: get(
                    response,
                    'Messages.0',
                    'Deal Expires'
                ),
                onOk() {
                    window.location.reload()
                },

            });
        } else {
            setCorpayInstructPaymentDetails({
                ...corpayInstructPaymentDetails,
                OrderId: response.content.orderNumber,
                AmountToBeSettled: spotRateDetails.amountToBeSettled,
                ClientAccessToken: bookDealDetails.clientAccessToken,
                CustomerPaidAmount: totalPayment,
                SelectedCurrency: selectedCurrency
            });
        }

    }

    /**
     * Listener function for when the panel's visibility changes.
     * Resets the form and selected keys if panel is closed.
     */
    const listenForClosingPanel = () => {
        if (!visible) {
            setDisplayCountdown(false)
            setSpotRateDetails({
                ...spotRateDetails
            });
        }
    };

    useEffect(listenForClosingPanel, [visible]);

    /**
     * Function called when submitting the form.
     */
    const handleSubmitForm = () => {
        setDisplayCountdown(false)
        setPayBtnDisabled(true)
        const payload = {
            "quoteId": bookDealDetails.quoteId,
            "clientAccessToken": bookDealDetails.clientAccessToken,
            "CompanyId": company.CompanyId
        }
        dispatch(
            bookDealRequestAction(
                payload,
                (response: DynamicObject) => {
                    getBookDealRequestCallback(response)
                }
            )
        );
    };


    const generateSectionWithLoading = (
        sectionComponent: any,
        loadingWidth: number,
        indicatorValue?: any
    ) => {
        if ((!indicatorValue)) {
            return (
                <Skeleton
                    className="notification-skeleton"
                    active={true}
                    loading
                    paragraph={true}
                    title={{ width: loadingWidth }}
                />
            );
        } else {
            return sectionComponent;
        }
    };

    const populateSpotRateDetails = () => {
        return (
            <>
                <Row>
                    <Col span={12}>
                        {!isEmpty(spotRateDetails) &&
                            <>
                                {spotRateDetails.spotRate != 0 &&
                                    <Row>
                                        <Col>
                                            The current rate is: <b>{parseFloat(spotRateDetails.spotRate).toFixed(2)} {currencyCode}</b>
                                        </Col>
                                    </Row>
                                }
                                {spotRateDetails.amountToBeSettled != 0 &&
                                    <Row>
                                        <Col>
                                            You will pay <b>{selectedCurrency} {formatAmountToBePaid(selectedCurrency, spotRateDetails.amountToBeSettled)}</b>
                                        </Col>
                                    </Row>
                                }
                            </>
                        }
                    </Col>
                    {displayCountdown &&
                        <>
                            <Col span={4}>
                                <CountDownTimer timeLimit={timeLimit} setTimesUp={setTimesUp} />
                            </Col>
                            <Col span={8}>The requested spot rate will expire in</Col>
                        </>
                    }
                </Row>

            </>

        );
    }

    /**
     * Function that populates the workflow options.
     */
    const populateCorpayCurrencyOptions = () => {
        return map(
            currencies,
            (currency: string) => (
                <Option key={currency} value={currency}>
                    {currency}
                </Option>
            )
        );
    };

    /**
     * Function for populating the panel content (with loading skeletons/data).
     */
    const populatePanelContent = () => {

        return (
            <>

                <Row>
                    <Row>
                        <img
                            className="corpay-logo-panel"
                            src={corpayLogo}
                            alt="Corpay Logo"
                        />
                    </Row>
                    <br />
                    <Row>
                        You are making a payment of {formatCurrency(totalPayment)}.
                    </Row>
                    <br />
                    <Row>
                        Select your preferred currency below then click the pay button to lock in the rate. You are then provided payment instructions via email.
                    </Row>
                    <br />
                    <Row>
                        <Col span={7}>
                            Preferred currency:
                        </Col>
                        <Col span={12}>
                            <Select
                                style={{ width: '100%' }}
                                onChange={onCorpayPaymentOptionChoose}
                                defaultValue="Select Currency"
                            >
                                {populateCorpayCurrencyOptions()}
                            </Select>
                        </Col>

                    </Row>

                </Row>
                <br />

                {generateSectionWithLoading(
                    populateSpotRateDetails(),
                    250,
                    spotRateDetails,
                )}
            </>
        );
    }

    return (
        <Row>
            <Col>
                {isMobile ? (
                    <div>
                        {visible && (
                            <Modal
                                className="modal-swapped-buttons modal-no-padding"
                                title={panelTitle}
                                visible={visible}
                                style={{ top: 12 }}
                                footer={false}
                            >
                                <div
                                    style={{
                                        maxHeight: window.innerHeight - 160,
                                        overflowY: 'auto',
                                        padding: 24,
                                    }}
                                >
                                    {
                                        generateSectionWithLoading(
                                            <>
                                                <div>{populatePanelContent()}</div>
                                                <br />

                                                <Row>
                                                    <Col className="ta-right" span={24}>
                                                        <Button
                                                            className="mr-8"
                                                            type="primary"
                                                            onClick={handleSubmitForm}
                                                            disabled={payBtnDisabled}
                                                        >
                                                            Pay using this rate
                                                        </Button>
                                                        <Button onClick={handleClosePanel}>
                                                            Cancel
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </>,
                                            250,
                                            totalPayment
                                        )
                                    }
                                </div>
                            </Modal>
                        )}
                    </div>
                ) : (
                    <Drawer
                        title={panelTitle}
                        width={500}
                        onClose={handleClosePanel}
                        visible={visible}
                        className="notification-details-panel"
                        closable={false}
                        maskClosable={false}
                    >

                        {visible && (
                            <>
                                {generateSectionWithLoading(
                                    <>
                                        <div>{populatePanelContent()}</div>
                                        <br />

                                        <Row>
                                            <Col className="ta-right" span={24}>
                                                <Button
                                                    className="mr-8"
                                                    type="primary"
                                                    onClick={handleSubmitForm}
                                                    disabled={payBtnDisabled}
                                                >
                                                    Pay using this rate
                                                </Button>
                                                <Button onClick={handleClosePanel}>
                                                    Cancel
                                                </Button>
                                            </Col>
                                        </Row>
                                    </>,
                                    250,
                                    totalPayment
                                )}

                            </>
                        )}

                    </Drawer>
                )}
            </Col>

            {spotRateLoading && (
                <Suspense fallback={null}>
                    <ModalWithSpinner
                        modalTitle="Requesting Spot Rate"
                        modalVisible={spotRateLoading}
                        displayMessage="Please wait while requesting the Spot Rate . . ."
                    />
                </Suspense>
            )}

        </Row>
    );
};

const NotificationCorpayPaymentAutoPanelForm = Form.create({
    name: 'notification-corpay-payment-auto-panel-form',
})(NotificationCorpayPaymentAutoPanel);

export default withRouter(
    withNumberFormatHandler(
        withDateFormatHandler(NotificationCorpayPaymentAutoPanelForm)
    )
);
